import * as Constants from "../../../utils/constants"
import { WebEditorContext, SelectableTextContext, TextLibraryContext, MetaforceContext } from "../../../contexts";
import { useContext, Fragment, useState } from "react"
import { EllipsisHorizontalCircleIcon } from '@heroicons/react/24/outline'
import { Menu, Transition, Popover } from "@headlessui/react";
import { useTranslation } from 'react-i18next';
import { resources, translations } from "../../../translations/translationsConstants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUndo, faRedo } from "@fortawesome/free-solid-svg-icons"
import "./styles.scss"
import ActionMenuButton from "./ActionMenuButton";

export default function ActionMenuBar() {
    const { startParameter, themeAndStyle } = useContext(MetaforceContext);
    const { undoRedoManager, actionMenuBar, setActionMenuBar, handleActionMenuBar, editorFileRef, onPreviewPdfClick, onPreviewHtmlClick, onPublishClick,
        onDiscardClick, isLoading, sideBarState, setSideBarState, languageCodeFromQueryString, handleUndo, handleRedo, setForcePreviewLoad, editorDisabled } = useContext(WebEditorContext);
    const { selectedTextPartIdRef } = useContext(SelectableTextContext);
    const { setSelectedEditorInsertPoint } = useContext(TextLibraryContext);
    const { t: tc } = useTranslation([resources.common]);
    const undoDisabled = !undoRedoManager.canUndo || editorFileRef.current.DxmlFileStatus !== Constants.fileStatus.draft || editorDisabled;
    const redoDisabled = !undoRedoManager.canRedo || editorFileRef.current.DxmlFileStatus !== Constants.fileStatus.draft || editorDisabled;

    const classNames = (state, additionalClassNames) => {
        const focusdClassName = " group-focus:tw-ring-2 group-focus:tw-ring-indigo-700 ";
        const disabledClassName = " group-disabled:tw-bg-gray-200 group-disabled:tw-text-gray-400 group-disabled:tw-cursor-not-allowed ";
        return "tw-h-9 tw-w-9 tw-flex tw-items-center tw-justify-center tw-px-2 tw-py-1 tw-border-gray-300 tw-leading-4 tw-font-medium tw-text-sm tw-rounded-sm " +
            " tw-shadow-sm sm:tw-border tw-text-gray-500 tw-bg-white tw-cursor-pointer hover:tw-bg-gray-50 " +
            focusdClassName + disabledClassName + additionalClassNames;
    }

    const mobileClassNames = (state, baseClassNames, active) => {
        if (state === Constants.disabledState || isLoading) {
            return `tw-outline-none tw-bg-gray-200 tw-text-gray-400 tw-cursor-not-allowed ${baseClassNames}`;
        } else if (state === Constants.focusedState || active) {
            return `tw-outline-none tw-ring-2 tw-ring-indigo-700 tw-text-gray-500 tw-bg-white ${baseClassNames}`;
        } else {
            return `tw-outline-none tw-text-gray-500 tw-bg-white tw-cursor-pointer hover:tw-bg-gray-50 focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-indigo-700 ${baseClassNames}`;
        }
    }

    const handleSelectableTextClick = () => {
        if (sideBarState === Constants.sideBarState.predefinedTexts) {
            setSideBarState(Constants.sideBarState.closed);
        } else {
            setSideBarState(Constants.sideBarState.predefinedTexts);
        }
        selectedTextPartIdRef.current = null;
        setSelectedEditorInsertPoint(null);

        handleActionMenuBar({
            editorFile: editorFileRef.current,
            actionMenuBar: {
                selectableTextState: sideBarState === Constants.sideBarState.predefinedTexts ? Constants.normalState : Constants.focusedState,
                textLibraryState: Constants.normalState,
                previewPdfState: Constants.normalState,
                previewHtmlState: Constants.normalState,
                publishState: Constants.normalState,
                discardState: Constants.normalState,
            },
        });
    }

    const handleTextLibrayClick = () => {
        if (sideBarState === Constants.sideBarState.textLibrary) {
            setSideBarState(Constants.sideBarState.closed);
        } else {
            setSideBarState(Constants.sideBarState.textLibrary);
        }
        selectedTextPartIdRef.current = null;
        setSelectedEditorInsertPoint(null);

        handleActionMenuBar({
            editorFile: editorFileRef.current,
            actionMenuBar: {
                selectableTextState: Constants.normalState,
                textLibraryState: sideBarState === Constants.sideBarState.textLibrary ? Constants.normalState : Constants.focusedState,
                previewPdfState: Constants.normalState,
                previewHtmlState: Constants.normalState,
                publishState: Constants.normalState,
                discardState: Constants.normalState,
            },
        });
    }

    const handlePreviewPdfClick = () => {
        onPreviewPdfClick();
    }

    const handlePreviewHtmlClick = () => {
        onPreviewHtmlClick();
    }

    const handlePublishClick = () => {
        onPublishClick();
    }

    const handleDiscardClick = () => {
        onDiscardClick();
    }

    const handleUndoClick = () => {
        handleUndo();
    }

    const handleRedoClick = () => {
        handleRedo();
    }

    const handleSplitScreenClick = () => {
        setActionMenuBar((prev) => ({
            ...prev,
            splitScreenState: actionMenuBar.splitScreenState === Constants.screenState.splitScreen ?
                Constants.screenState.singleScreen : Constants.screenState.splitScreen,
        }));
        setForcePreviewLoad(0);
    }

    const actionButtons = [
        {
            hide: themeAndStyle?.actionButtons?.predefined?.hide,
            label: themeAndStyle?.actionButtons?.predefined?.text || tc(translations.common.predefined),
            state: actionMenuBar.selectableTextState,
            disabled: actionMenuBar.selectableTextState === Constants.disabledState || isLoading,
            className: themeAndStyle?.actionButtons?.predefined?.className,
            onClick: () => handleSelectableTextClick(),
            icon: themeAndStyle?.actionButtons?.predefined?.icon ?
                (
                    themeAndStyle?.actionButtons?.predefined?.icon
                )
                :
                (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={classNames(actionMenuBar.selectableTextState)}
                        fill="currentColor"
                        stroke="currentColor"
                        viewBox="0 0 512 512"
                    >
                        <path d="M464 32H48C21.49 32 0 53.49 0 80v352c0 26.51 21.49 48 48 48h416c26.51 0 48-21.49 48-48V80c0-26.51-21.49-48-48-48zm-6 400H54a6 6 0 0 1-6-6V86a6 6 0 0 1 6-6h404a6 6 0 0 1 6 6v340a6 6 0 0 1-6 6zm-42-92v24c0 6.627-5.373 12-12 12H204c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h200c6.627 0 12 5.373 12 12zm0-96v24c0 6.627-5.373 12-12 12H204c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h200c6.627 0 12 5.373 12 12zm0-96v24c0 6.627-5.373 12-12 12H204c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h200c6.627 0 12 5.373 12 12zm-252 12c0 19.882-16.118 36-36 36s-36-16.118-36-36 16.118-36 36-36 36 16.118 36 36zm0 96c0 19.882-16.118 36-36 36s-36-16.118-36-36 16.118-36 36-36 36 16.118 36 36zm0 96c0 19.882-16.118 36-36 36s-36-16.118-36-36 16.118-36 36-36 36 16.118 36 36z" />
                    </svg>
                ),
        },
        {
            hide: themeAndStyle?.actionButtons?.library?.hide,
            label: themeAndStyle?.actionButtons?.library?.text || tc(translations.common.library),
            state: actionMenuBar.textLibraryState,
            disabled: actionMenuBar.textLibraryState === Constants.disabledState || isLoading,
            className: themeAndStyle?.actionButtons?.library?.className,
            onClick: () => handleTextLibrayClick(),
            icon: themeAndStyle?.actionButtons?.library?.icon ?
                (
                    themeAndStyle?.actionButtons?.library?.icon
                )
                :
                (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={classNames(actionMenuBar.textLibraryState)}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                    </svg>
                ),
        },
        {
            hide: themeAndStyle?.actionButtons?.preview?.hide,
            label: themeAndStyle?.actionButtons?.preview?.text || tc(translations.common.preview),
            state: actionMenuBar.previewPdfState,
            disabled: actionMenuBar.previewPdfState === Constants.disabledState || isLoading,
            className: themeAndStyle?.actionButtons?.preview?.className,
            onClick: () => handlePreviewPdfClick(),
            icon: themeAndStyle?.actionButtons?.preview?.icon ?
                (
                    themeAndStyle?.actionButtons?.preview?.icon
                )
                :
                (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={classNames(actionMenuBar.previewPdfState)}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 21h7a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v11m0 5l4.879-4.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242z" />
                    </svg>
                ),
            isPopover: editorFileRef.current?.WebEditorSettings?.PreviewHtmlEnabled,
            popoverOptions: [{
                label: tc(translations.common.previewPDF),
                onClick: () => handlePreviewPdfClick(),
                state: actionMenuBar.previewPdfState,
            },
            {
                label: tc(translations.common.previewHTML),
                onClick: () => handlePreviewHtmlClick(),
                state: actionMenuBar.previewHtmlState,
            }],
        },
        {
            hide: themeAndStyle?.actionButtons?.splitScreen?.hide,
            label: actionMenuBar.splitScreenState === Constants.screenState.singleScreen ?
                (themeAndStyle?.actionButtons?.splitScreen?.onText || tc(translations.common.splitScreen))
                :
                (themeAndStyle?.actionButtons?.splitScreen?.offText || tc(translations.common.singleScreen)),
            state: actionMenuBar.splitScreenState,
            disabled: actionMenuBar.splitScreenState === Constants.disabledState || isLoading,
            className: themeAndStyle?.actionButtons?.splitScreen?.className,
            onClick: () => handleSplitScreenClick(),
            icon: actionMenuBar.splitScreenState === Constants.screenState.singleScreen
                ?
                (themeAndStyle?.actionButtons?.splitScreen?.onIcon ||
                    <svg xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        className={classNames(actionMenuBar.splitScreenState)}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path d="M8 19H5c-1 0-2-1-2-2V7c0-1 1-2 2-2h3" />
                        <path d="M16 5h3c1 0 2 1 2 2v10c0 1-1 2-2 2h-3" />
                        <line x1="12" x2="12" y1="4" y2="20" />
                    </svg>)
                :
                (themeAndStyle?.actionButtons?.splitScreen?.offIcon ||
                    <svg xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        className={classNames(actionMenuBar.splitScreenState)}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <rect width="18" height="18" x="3" y="3" rx="2" />
                    </svg>),
        },
        {
            hide: themeAndStyle?.actionButtons?.undo?.hide,
            label: themeAndStyle?.actionButtons?.undo?.text || tc(translations.common.format.undo),
            state: actionMenuBar.undoState,
            disabled: undoDisabled,
            className: themeAndStyle?.actionButtons?.undo?.className,
            onClick: () => handleUndoClick(),
            icon: <div className={classNames(!undoDisabled ? Constants.normalState : Constants.disabledState)}>
                <FontAwesomeIcon
                    className="tw-h-4 tw-w-4"
                    icon={faUndo}
                />
            </div>,
        },
        {
            hide: themeAndStyle?.actionButtons?.redo?.hide,
            label: themeAndStyle?.actionButtons?.redo?.text || tc(translations.common.format.redo),
            state: actionMenuBar.redoState,
            disabled: redoDisabled,
            className: themeAndStyle?.actionButtons?.redo?.className,
            onClick: () => handleRedoClick(),
            icon: <div className={classNames(!redoDisabled ? Constants.normalState : Constants.disabledState)}>
                <FontAwesomeIcon
                    className="tw-h-4 tw-w-4"
                    icon={faRedo}
                />
            </div>,
        },
        {
            hide: themeAndStyle?.actionButtons?.publish?.hide,
            label: themeAndStyle?.actionButtons?.publish?.text || tc(translations.common.publish),
            state: actionMenuBar.publishState,
            disabled: actionMenuBar.publishState === Constants.disabledState || isLoading,
            className: themeAndStyle?.actionButtons?.publish?.className,
            onClick: () => handlePublishClick(),
            icon: themeAndStyle?.actionButtons?.publish?.icon ?
                (
                    themeAndStyle?.actionButtons?.publish?.icon
                )
                :
                (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={classNames(actionMenuBar.publishState,
                            ((editorFileRef.current?.DxmlFileStatus === Constants.fileStatus.ok) ? " tw-ring-2 tw-ring-indigo-700 " : ""))}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                ),
        },
        {
            hide: themeAndStyle?.actionButtons?.discard?.hide,
            label: themeAndStyle?.actionButtons?.discard?.text || tc(translations.common.discard),
            state: actionMenuBar.discardState,
            disabled: actionMenuBar.discardState === Constants.disabledState || isLoading,
            className: themeAndStyle?.actionButtons?.discard?.className,
            onClick: () => handleDiscardClick(),
            icon: themeAndStyle?.actionButtons?.discard?.icon ?
                (
                    themeAndStyle?.actionButtons?.discard?.icon
                )
                :
                (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={classNames(actionMenuBar.discardState,
                            ((editorFileRef.current?.DxmlFileStatus === Constants.fileStatus.discarded) ? " tw-ring-2 tw-ring-indigo-700 " : ""))}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                ),
        },
    ];

    return (
        <>
            <div className="tw-hidden sm:tw-inline-flex tw-px-8">
                <div className="tw-inline-flex tw-gap-5">
                    {actionButtons.map((actionButton, index) => (
                        <ActionMenuButton
                            key={index}
                            hideActionButtonText={themeAndStyle?.hideActionButtonText}
                            {...actionButton}
                        />
                    ))}
                </div>
            </div>

            {/* Mobile menu button */}
            <div className="tw-relative sm:tw-hidden tw-h-6 tw-w-6 tw-px-3">
                <Menu as="div" className="tw-ml-4 tw-relative tw-flex-shrink-0">
                    <div>
                        <Menu.Button
                            disabled={isLoading || editorFileRef.current?.DxmlFileStatus !== Constants.fileStatus.draft}
                            className="menu-button-tooltip-container tw-group tw-h-6 tw-w-6 tw-rounded-md tw-inline-flex tw-items-center tw-text-base tw-font-medium">
                            <span className="tw-sr-only">{tc(translations.common.openMenu)}</span>
                            <EllipsisHorizontalCircleIcon className="tw-h-6 tw-w-6 tw-text-gray-700" aria-hidden="true" />
                            <span role="tooltip" id="moreoptions-action-tooltip-desc" className="menu-tooltip">{tc(translations.common.moreoptions)}</span>
                        </Menu.Button>
                    </div>
                    <Transition
                        as={Fragment}
                        enter="tw-transition tw-ease-out tw-duration-100"
                        enterFrom="tw-transform tw-opacity-0 tw-scale-95"
                        enterTo="tw-transform tw-opacity-100 tw-scale-100"
                        leave="tw-transition tw-ease-in tw-duration-75"
                        leaveFrom="tw-transform tw-opacity-100 tw-scale-100"
                        leaveTo="tw-transform tw-opacity-0 tw-scale-95"
                    >
                        <Menu.Items className="tw-origin-top-left tw-absolute tw-left-0 tw-w-56 tw-rounded tw-shadow-lg tw-px-2 tw-py-1 tw-bg-white tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none tw-transform tw-opacity-100 tw-scale-100 tw-space-y-2">
                            {actionButtons
                                .filter(x => !x.hide)
                                .reduce((prev, curr, i) => {
                                    if (curr.isPopover) {
                                        curr.popoverOptions.forEach((option) => {
                                            const newOption = {
                                                ...curr,
                                                label: option.label,
                                                onClick: option.onClick,
                                                state: option.state,
                                            }
                                            prev.push(newOption);
                                        })
                                    } else {
                                        prev.push(curr);
                                    }
                                    return prev;
                                }, [])
                                .map((actionButton, index) => (
                                    <Menu.Item key={index}>
                                        {({ active }) => (
                                            <button
                                                type="button"
                                                className={mobileClassNames(actionButton.state, "tw-group tw-block tw-w-full tw-text-sm tw-font-medium tw-text-left tw-py-1 tw-rounded-md", active)}
                                                onClick={actionButton.onClick}
                                                disabled={actionButton.disabled}
                                            >
                                                <div class="tw-flex tw-items-center tw-w-full tw-rounded-md tw-gap-x-3" >
                                                    {actionButton.icon}
                                                    <span className="tw-truncate">{actionButton.label}</span>
                                                </div>
                                            </button>
                                        )}
                                    </Menu.Item>
                                ))}
                        </Menu.Items>
                    </Transition>
                </Menu>
            </div>
        </>
    )
}